import SiteSearch from './site-search';

/**
 * Handle quick site search UI.
 *
 * @class QuickSiteSearch
 * @param {HTMLDivElement} searchContainer - Container element for quick site search UI.
 */

class QuickSiteSearch extends SiteSearch {
  constructor(searchContainer) {
    super('quick-search');

    this.elements = {
      searchContainer,
      openSearchButton: document.getElementById('nav-site-search-open'),
      closeSearchButton: document.getElementById('nav-site-search-close'),
      searchForm: document.getElementById('nav-site-search-form'),
      searchInput: document.getElementById('nav-site-search-input'),
      searchResults: document.getElementById('nav-site-search-results'),
      progressBar: document.getElementById('nav-site-search-progress-bar'),
      resultCategories: this.getSearchResultElements('search-results-category'),
    };

    this.templates = this.getSearchResultElements('search-results-list-template', true);

    this.addEventListeners();
  }

  /**
   * Add all events listeners.
   *
   * @return {undefined}
   */

  addEventListeners() {
    this.elements.openSearchButton.addEventListener('click', () => this.openSearchForm());
    this.elements.closeSearchButton.addEventListener('click', () => this.closeSearchForm());
    this.elements.searchInput.addEventListener('input', event => this.onSearchInput(event.target.value));
    this.elements.searchInput.addEventListener('keydown', event => this.onSearchKeydown(event.key));
    document.addEventListener('click', event => this.onDocumentClick(event.target));

    this.registerSearchResultEvents();
  }

  /**
   * Open search form.
   *
   * @return {undefined}
   */

  openSearchForm() {
    if (this.elements.openSearchButton.hidden) {
      this.closeSearchForm();
    } else {
      document.querySelector('body').classList.add('search-open');
      this.elements.openSearchButton.hidden = true;
      this.elements.searchForm.hidden = false;
      this.elements.searchResults.hidden = !this.elements.searchInput.value.length;
      this.elements.searchInput.focus();
    }
  }

  /**
   * Close search form.
   *
   * @return {undefined}
   */

  closeSearchForm() {
    document.querySelector('body').classList.remove('search-open');
    this.inputTimer && clearTimeout(this.inputTimer);
    this.elements.openSearchButton.hidden = false;
    this.elements.searchForm.hidden = true;
    this.elements.searchResults.hidden = true;
  }

  /**
   * Fetch and render search results, with debounce.
   *
   * @return {undefined}
   */

  onSearchInput(searchTerm, searchDelay = 500) {
    this.inputTimer && clearTimeout(this.inputTimer);

    this.inputTimer = setTimeout(() => {
      this.elements.searchResults.hidden = false;
      this.toggleProgressBarVisibility(true);
      this.getSearchResults(searchTerm)
        .then(searchResults => {
          this.toggleProgressBarVisibility(false);
          this.setCarsCategoryContainerWidth(searchResults.hits.cars.length);
          this.renderSearchResults(searchResults);
        });
    }, searchDelay);
  }

  /**
   * Close search form, if key is Esc.
   *
   * @param  {string}    key - Key name.
   * @return {undefined}
   */

  onSearchKeydown(key) {
    if (key === 'Escape' || key === 'Esc') {
      this.closeSearchForm();
    }
  }

  /**
   * Close search form, if open, and target is not within search container.
   *
   * @param  {EventTarget} target - Event target.
   * @return {undefined}
   */

  onDocumentClick(target) {
    if (!this.elements.searchForm.hidden && !this.elements.searchContainer.contains(target)) {
      this.closeSearchForm();
    }
  }

  /**
   * Toggle helper class for cars category container, based on number of results.
   *
   * @param  {number}    carsLength             - Number of car results.
   * @param  {number}    lengthThreshold        - Min. result count needed for helper class.
   * @param  {string}    wideContainerClassName - Helper class name.
   * @return {undefined}
   */

  setCarsCategoryContainerWidth(carsLength, lengthThreshold = 4, wideContainerClassName = 'wide-container') {
    const carsCategoryContainer = this.elements.resultCategories.cars.parentElement;

    if (carsLength >= lengthThreshold) {
      carsCategoryContainer.classList.add(wideContainerClassName);
    }
    else if (carsCategoryContainer.classList.contains(wideContainerClassName)) {
      carsCategoryContainer.classList.remove(wideContainerClassName);
    }
  }
}

export default QuickSiteSearch;
